import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { apiCall } from '../utils/api';

const REVIEW = {
  pending_review: ['Awaiting review', 'bg-slate-100 text-slate-700'],
  confirmed: ['Confirmed', 'bg-green-100 text-green-700'],
  rejected: ['Rejected', 'bg-red-100 text-red-700'],
  reclassified: ['Reclassified', 'bg-purple-100 text-purple-700'],
};
const DELIVERY = {
  pending: ['Sending to WSafe.SI', 'bg-blue-50 text-blue-700'],
  sent: ['In WSafe.SI queue', 'bg-blue-100 text-blue-700'],
  failed: ['Not delivered', 'bg-amber-100 text-amber-800'],
  not_configured: ['WSafe.SI not connected', 'bg-amber-100 text-amber-800'],
};

const Pill = ({ label, cls, testId }) => (
  <span data-testid={testId} className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>
);

export const WsafeReviewStatus = ({ sourceType, sourceId, onReviewChange }) => {
  const [cand, setCand] = useState(null);
  const lastReview = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await apiCall(`/api/wsafe-si/candidates?source_type=${sourceType}&source_id=${encodeURIComponent(sourceId)}&limit=1`);
      setCand(data.candidates[0] || null);
    } catch {
      setCand(null);
    }
  }, [sourceType, sourceId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const review = cand?.review_status;
    if (!review) return;
    if (lastReview.current && lastReview.current !== review && onReviewChange) onReviewChange(review);
    lastReview.current = review;
  }, [cand, onReviewChange]);

  if (!cand) return null;
  const tid = `wsafe-status-${sourceId}`;
  const retry = async () => setCand(await apiCall(`/api/wsafe-si/candidates/${cand.id}/retry`, { method: 'POST' }));
  const showDelivery = cand.review_status === 'pending_review';
  const [rLabel, rCls] = REVIEW[cand.review_status] || REVIEW.pending_review;
  const [dLabel, dCls] = DELIVERY[cand.delivery_status] || DELIVERY.pending;

  return (
    <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={tid}>
      <span className="text-xs text-slate-500">Competent person review:</span>
      <Pill label={rLabel} cls={rCls} testId={`${tid}-review`} />
      {cand.review_status === 'reclassified' && cand.reviewed_class && (
        <Pill label={`as ${cand.reviewed_class.replace(/_/g, ' ')}`} cls="bg-purple-50 text-purple-700" testId={`${tid}-class`} />
      )}
      {showDelivery && <Pill label={dLabel} cls={dCls} testId={`${tid}-delivery`} />}
      {showDelivery && ['failed', 'not_configured'].includes(cand.delivery_status) && (
        <button onClick={retry} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1" data-testid={`${tid}-retry`}>
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      )}
      {cand.ticket_status && (
        <Pill label={`HazardLoop ticket ${cand.ticket_status}`} cls={cand.ticket_status === 'closed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'} testId={`${tid}-ticket`} />
      )}
      {cand.ticket_url && (
        <a href={cand.ticket_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1" data-testid={`${tid}-ticket-link`}>
          Open ticket <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
};

export default WsafeReviewStatus;
